<script setup lang="ts">
import { useDark } from '@vueuse/core'
import { onUnmounted, ref, watch } from 'vue'

/**
 * Renders one mermaid diagram from its source.
 *
 * mermaid is lazy-imported (heavy; only pulled when a diagram actually appears)
 * and rendered to SVG. While a reply streams in, the source can be transiently
 * invalid — render errors are swallowed and the last good SVG is kept, so a
 * half-typed diagram doesn't flash an error. If it never becomes valid, the raw
 * source stays visible as a fallback `<pre>`.
 */

const props = defineProps<{
  code: string
}>()

const isDark = useDark()
const svg = ref('')
const failed = ref(false)

// A stable-ish unique id per instance; mermaid needs a DOM id to render into.
const renderId = `mermaid-${Math.random().toString(36).slice(2)}`

let seq = 0
let debounce: ReturnType<typeof setTimeout> | undefined

async function render() {
  const code = props.code.trim()
  if (!code) {
    svg.value = ''
    return
  }
  const token = ++seq
  try {
    const mermaid = (await import('mermaid')).default
    mermaid.initialize({ startOnLoad: false, theme: isDark.value ? 'dark' : 'default', securityLevel: 'strict' })
    // parse() throws on incomplete/invalid source without touching the DOM,
    // so we gate render() on it to avoid mermaid injecting error graphics.
    await mermaid.parse(code)
    const { svg: out } = await mermaid.render(`${renderId}-${token}`, code)
    if (token === seq) {
      svg.value = out
      failed.value = false
    }
  }
  catch {
    // Streaming/invalid source: keep the previous good SVG if any; otherwise
    // fall back to showing the raw source.
    if (token === seq && !svg.value)
      failed.value = true
  }
}

watch([() => props.code, isDark], () => {
  clearTimeout(debounce)
  debounce = setTimeout(render, 120)
}, { immediate: true })

onUnmounted(() => clearTimeout(debounce))
</script>

<template>
  <div :class="['my-2', 'flex justify-center overflow-x-auto']">
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div v-if="svg && !failed" class="mermaid-rendered" v-html="svg" />
    <pre v-else :class="['w-full overflow-x-auto rounded-md p-3', 'bg-neutral-100 dark:bg-neutral-800']"><code>{{ code }}</code></pre>
  </div>
</template>
